import { TimeService } from "../domain/useTime";
import {
    calcDataRangeFilters,
    CalcDataRangeFilters,
    extractMetadataFiltersFromAxiosResponse,
    extractResultCountFromAxiosResponse,
    ExtractResultFn,
    extractResultsFromAxiosResponse,
    extractResultThen,
    mostRelevant,
} from "./search";
import { AxiosStatic } from "axios";
import { filterQuery, FilterQuery } from "../domain/info";
import { QueryTypeAnd } from "../interfaces/ElasticSearchInterface";
import { EnvironmentConfig } from "@me8eon/config";
import { JwtTokenFn } from "@me8eon/authentication";

export type RawCallElasticSearchFn = (
    url: string,
    body: any,
    apiKey: JwtTokenFn,
) => Promise<any>;
export type ApiKeyFn = () => string | null;

// export function defaultApiKeyFn(): string | null {
//     return localStorage.getItem("dls-apikey");
// }

export type SearchContext = {
    timeService: TimeService;
    indicies: string;
    elasticsearchUrl: (indicies: string, explain?: boolean) => string;
    rawCallElasticSearch: RawCallElasticSearchFn;
    extractResults: QueryTypeAnd<ExtractResultFn>;
    extractResultCount?: (response: any) => any;
    extractMetadataFilters: (response: any) => any;
    calcDataRangeFilters: CalcDataRangeFilters;
    filterQuery: FilterQuery;
    boosts?: string[];
    embeddingModel?: string;
};

//  elasticSearchDomain is likely to be one of
//  https://c3224bc073f74e73b4d7cec2bb0d5b5e.westeurope.azure.elastic-cloud.com:9243/
// https://f0571c62200b4d249a4c6750ab7f4716.westeurope.azure.elastic-cloud.com:9243/
export const defaultSearchContext = (
    axios: AxiosStatic,
): SearchContext => ({
    timeService: () => new Date(),
    indicies: EnvironmentConfig.indicies as string,
    embeddingModel: ".multilingual-e5-small_linux-x86_64",
    elasticsearchUrl: (indicies, explain?: boolean) => {
        function mapSourceToIndex(source: string): string {
            const mapping: Record<string, string> = {
                "Jira": "jira-prod",
                "Confluence": "confluence-prod",
                "Gitlab": "gitlab-prod",
                "MyGenius": "mygenius-api",
                "OrderIT": "order-it-api",
            };
            return mapping[source];
        }
        const suffix = explain ? "_explain" : `_search`;
        let actualIndicies;
        if (window.location.href.includes("me8eon1174=true")) {
            const userSource: boolean = true;

            let userIndices: string;
            if (userSource) {
                userIndices = mapSourceToIndex("MyGenius");
            } else {
                userIndices = null as any;
            }
            if (userIndices) {
                actualIndicies = explain ? userIndices.split(",")[0] : userIndices;
            } else {
                actualIndicies = explain ? indicies.split(",")[0] : indicies;
            }
        } else {
            actualIndicies = explain ? indicies.split(",")[0] : indicies;
        }

        return `${EnvironmentConfig.elasticSearchProxyUrl}/${actualIndicies}/${suffix}`;
    },
    rawCallElasticSearch: async (url: string, body: any, apiKey) => {
        if (typeof apiKey !== "function")
            throw new Error("apiKey is not a function", apiKey);
        return axios.post(url, body, {
            headers: { Authorization: `Bearer ${await apiKey()}` },
        });
    },
    extractResults: {
        knn: extractResultsFromAxiosResponse,
        boostAndPhrase: extractResultsFromAxiosResponse,
        keywords: extractResultThen(
            extractResultsFromAxiosResponse,
            mostRelevant(0.7),
        ),
    },
    extractResultCount: extractResultCountFromAxiosResponse,
    extractMetadataFilters: extractMetadataFiltersFromAxiosResponse,
    calcDataRangeFilters,
    filterQuery,
    boosts: [
        // "full_text", deliberately removed
        `issue`, //the boosts are all removed
        `title`,
        `name`,
        `card_title`,
    ],
});

